export type EventContextField = {
  path: string
  label: string
  type?: string
  description?: string
  children?: EventContextField[]
}

const ENVELOPE_FIELDS: EventContextField[] = [
  {
    path: 'resource',
    label: 'Resource',
    type: 'object',
    children: [
      { path: 'resource.type', label: 'Type', type: 'string', description: 'thread, contact, instance, app' },
      { path: 'resource.id', label: 'ID', type: 'string' },
      {
        path: 'resource.communicationChannelId',
        label: 'Communication channel ID',
        type: 'string',
        description: 'Present on thread message events',
      },
    ],
  },
  {
    path: 'actor',
    label: 'Actor',
    type: 'object',
    children: [
      { path: 'actor.type', label: 'Type', type: 'string' },
      { path: 'actor.id', label: 'ID', type: 'string' },
      { path: 'actor.name', label: 'Name', type: 'string' },
      { path: 'actor.isHuman', label: 'Is human', type: 'boolean' },
    ],
  },
  {
    path: 'meta',
    label: 'Meta',
    type: 'object',
    children: [
      { path: 'meta.timestamp', label: 'Timestamp', type: 'string' },
      {
        path: 'meta.isFirstOccurrence',
        label: 'Is first occurrence',
        type: 'boolean',
        description: 'thread.message.received only',
      },
      { path: 'meta.correlationId', label: 'Correlation ID', type: 'string' },
    ],
  },
]

const MESSAGE_DATA_FIELDS: EventContextField[] = [
  {
    path: 'data.message',
    label: 'Message',
    type: 'object',
    children: [
      { path: 'data.message.id', label: 'Message ID', type: 'string' },
      { path: 'data.message.content', label: 'Content', type: 'string' },
      { path: 'data.message.contentType', label: 'Content type', type: 'string' },
      {
        path: 'data.message.attachments',
        label: 'Attachments',
        type: 'array',
        description:
          'Files on the message. Each item has fileId, name, mimeType, url',
        children: [
          { path: 'data.message.attachments.fileId', label: 'File ID', type: 'string' },
          { path: 'data.message.attachments.name', label: 'File name', type: 'string' },
          {
            path: 'data.message.attachments.mimeType',
            label: 'MIME type',
            type: 'string',
            description: 'e.g. audio/mpeg, audio/mp4, image/jpeg',
          },
          { path: 'data.message.attachments.url', label: 'URL', type: 'string' },
        ],
      },
    ],
  },
  {
    path: 'data.participant',
    label: 'Participant',
    type: 'object',
    children: [
      { path: 'data.participant.id', label: 'Participant ID', type: 'string' },
      {
        path: 'data.participant.kind',
        label: 'Kind',
        type: 'string',
        description: 'CONTACT | MEMBER | AGENT | WORKFLOW',
      },
      { path: 'data.participant.displayName', label: 'Display name', type: 'string' },
    ],
  },
  { path: 'data.messageCount', label: 'Message count', type: 'number' },
]

const THREAD_CREATED_DATA: EventContextField[] = [
  {
    path: 'data.thread',
    label: 'Thread',
    type: 'object',
    children: [
      { path: 'data.thread.id', label: 'Thread ID', type: 'string' },
      { path: 'data.thread.title', label: 'Title', type: 'string' },
      { path: 'data.thread.kind', label: 'Kind', type: 'string' },
      { path: 'data.thread.status', label: 'Status', type: 'string' },
      { path: 'data.thread.parentId', label: 'Parent ID', type: 'string' },
    ],
  },
  {
    path: 'data.participants',
    label: 'Participants',
    type: 'array',
    description: 'Array of participant objects',
  },
]

const PARTICIPANT_JOINED_DATA: EventContextField[] = [
  {
    path: 'data.participant',
    label: 'Participant',
    type: 'object',
    children: [
      { path: 'data.participant.id', label: 'Participant ID', type: 'string' },
      { path: 'data.participant.threadParticipantId', label: 'Thread participant ID', type: 'string' },
      { path: 'data.participant.kind', label: 'Kind', type: 'string' },
      { path: 'data.participant.displayName', label: 'Display name', type: 'string' },
    ],
  },
]

const CONTEXT_CHANGED_DATA: EventContextField[] = [
  { path: 'data.before', label: 'Before', type: 'object' },
  { path: 'data.after', label: 'After', type: 'object' },
  { path: 'data.changedKeys', label: 'Changed keys', type: 'array' },
]

const SIGNAL_CREATED_DATA: EventContextField[] = [
  { path: 'data.blockId', label: 'Block ID', type: 'string' },
  { path: 'data.description', label: 'Description', type: 'string' },
  {
    path: 'data.handle',
    label: 'Handle',
    type: 'string',
    description: 'Stable routing handle from contact.signal.create / thread.signal.create',
  },
  { path: 'data.appHandle', label: 'App handle', type: 'string' },
  { path: 'data.appName', label: 'App name', type: 'string' },
  {
    path: 'data.contactId',
    label: 'Contact ID',
    type: 'string',
    description: 'Present when the signal was created on a contact',
  },
  { path: 'data.metadata', label: 'Metadata', type: 'object' },
]

const MESSAGE_SENT_EXTRA: EventContextField[] = [
  {
    path: 'data.deliveredTo',
    label: 'Delivered to',
    type: 'object',
    children: [
      { path: 'data.deliveredTo.identifierValue', label: 'Identifier value', type: 'string' },
    ],
  },
]

const ATTACHMENT_RECEIVED_DATA: EventContextField[] = [
  {
    path: 'data.message',
    label: 'Message',
    type: 'object',
    children: [
      { path: 'data.message.id', label: 'Message ID', type: 'string' },
    ],
  },
  {
    path: 'data.attachment',
    label: 'Attachment',
    type: 'object',
    children: [
      { path: 'data.attachment.id', label: 'Attachment ID', type: 'string' },
      { path: 'data.attachment.fileId', label: 'File ID', type: 'string' },
      { path: 'data.attachment.name', label: 'File name', type: 'string' },
      {
        path: 'data.attachment.mimeType',
        label: 'MIME type',
        type: 'string',
        description: 'e.g. audio/mpeg, audio/wav, image/jpeg',
      },
      { path: 'data.attachment.size', label: 'Size (bytes)', type: 'number' },
      { path: 'data.attachment.url', label: 'URL', type: 'string' },
    ],
  },
  {
    path: 'data.participant',
    label: 'Participant',
    type: 'object',
    children: [
      { path: 'data.participant.id', label: 'Participant ID', type: 'string' },
      {
        path: 'data.participant.kind',
        label: 'Kind',
        type: 'string',
        description: 'CONTACT | MEMBER | AGENT | WORKFLOW',
      },
      { path: 'data.participant.displayName', label: 'Display name', type: 'string' },
    ],
  },
]

const CRM_INSTANCE_CREATED: EventContextField[] = [
  { path: 'data.model', label: 'Model name', type: 'string' },
  { path: 'data.record', label: 'Record', type: 'object' },
]

const CRM_INSTANCE_UPDATED: EventContextField[] = [
  { path: 'data.model', label: 'Model name', type: 'string' },
  {
    path: 'data.record',
    label: 'Record',
    type: 'object',
    description: 'Current record (same as after)',
  },
  { path: 'data.before', label: 'Before', type: 'object' },
  { path: 'data.after', label: 'After', type: 'object' },
  { path: 'data.changedFields', label: 'Changed fields', type: 'array' },
]

const CONTACT_DATA: EventContextField[] = [
  {
    path: 'data.contact',
    label: 'Contact',
    type: 'object',
    children: [
      { path: 'data.contact.id', label: 'Contact ID', type: 'string' },
      { path: 'data.contact.displayName', label: 'Display name', type: 'string' },
      { path: 'data.contact.identifiers', label: 'Identifiers', type: 'array' },
    ],
  },
  { path: 'data.changedFields', label: 'Changed fields', type: 'array', description: 'contact.updated only' },
]

/** Envelope fields shared by all `app.*` events. Payload fields come from the app catalog. */
const APP_EVENT_BASE: EventContextField[] = [
  { path: 'data.eventName', label: 'Event name', type: 'string' },
  { path: 'data.eventType', label: 'Event type', type: 'string' },
  { path: 'data.appInstallationId', label: 'App installation ID', type: 'string' },
]

const EVENT_DATA_FIELDS: Record<string, EventContextField[]> = {
  'thread.message.received': MESSAGE_DATA_FIELDS,
  'thread.message.sent': [...MESSAGE_DATA_FIELDS, ...MESSAGE_SENT_EXTRA],
  'thread.attachment.received': ATTACHMENT_RECEIVED_DATA,
  'thread.created': THREAD_CREATED_DATA,
  'thread.participant.joined': PARTICIPANT_JOINED_DATA,
  'thread.context.changed': CONTEXT_CHANGED_DATA,
  'thread.signal.created': SIGNAL_CREATED_DATA,
  'instance.created': CRM_INSTANCE_CREATED,
  'instance.updated': CRM_INSTANCE_UPDATED,
  'instance.deleted': CRM_INSTANCE_CREATED,
  'contact.created': CONTACT_DATA,
  'contact.updated': CONTACT_DATA,
}

const SAMPLE_ENVELOPES: Record<string, Record<string, unknown>> = {
  'thread.message.received': {
    resource: { type: 'thread', id: 'thd_example' },
    actor: { type: 'participant', id: 'tp_example', name: 'Jane Doe', isHuman: true },
    data: {
      message: {
        id: 'msg_example',
        content: 'Hello',
        contentType: 'text/plain',
        attachments: [
          {
            fileId: 'fl_example',
            name: 'voice-memo.m4a',
            mimeType: 'audio/mp4',
            url: 'https://example.com/voice-memo.m4a',
          },
        ],
      },
      participant: { id: 'part_example', kind: 'CONTACT', displayName: 'Jane Doe' },
      messageCount: 1,
    },
    meta: { timestamp: '2026-01-01T00:00:00.000Z', isFirstOccurrence: true },
  },
  'thread.message.sent': {
    resource: { type: 'thread', id: 'thd_example' },
    actor: { type: 'member', id: 'tp_example', name: 'Support Agent', isHuman: true },
    data: {
      message: {
        id: 'msg_example',
        content: 'Reply',
        contentType: 'text/plain',
        attachments: [],
      },
      participant: { id: 'part_example', kind: 'MEMBER', displayName: 'Support Agent' },
      deliveredTo: { identifierValue: '+15551234567' },
    },
    meta: { timestamp: '2026-01-01T00:00:00.000Z' },
  },
  'thread.attachment.received': {
    resource: { type: 'thread', id: 'thd_example' },
    actor: { type: 'contact', id: 'tp_example', name: 'Jane Doe', isHuman: true },
    data: {
      message: { id: 'msg_example' },
      attachment: {
        id: 'thdatt_example',
        fileId: 'fl_example',
        name: 'voice-memo.m4a',
        mimeType: 'audio/mp4',
        size: 96000,
      },
      participant: { id: 'part_example', kind: 'CONTACT', displayName: 'Jane Doe' },
    },
    meta: { timestamp: '2026-01-01T00:00:00.000Z' },
  },
  'thread.created': {
    resource: { type: 'thread', id: 'thd_example' },
    data: {
      thread: { id: 'thd_example', title: 'New thread', kind: 'INTERNAL', status: 'OPEN' },
      participants: [{ id: 'part_example', kind: 'CONTACT', displayName: 'Jane Doe' }],
    },
    meta: { timestamp: '2026-01-01T00:00:00.000Z' },
  },
  'thread.signal.created': {
    resource: { type: 'thread', id: 'thd_example' },
    actor: { type: 'workflow', id: 'wfr_example', isHuman: false },
    data: {
      blockId: 'tb_example',
      description: 'Signed up',
      handle: 'member-signed-up',
      appHandle: 'acme',
      appName: 'Acme',
      contactId: 'ctc_example',
      metadata: { eventType: 'app.acme.member.created' },
    },
    meta: { timestamp: '2026-01-01T00:00:00.000Z' },
  },
}

function parseAppEventType(
  eventType: string,
): { appHandle: string; eventName: string } | null {
  if (!eventType.startsWith('app.')) {
    return null
  }
  const parts = eventType.split('.')
  if (parts.length < 3) {
    return null
  }
  return {
    appHandle: parts[1]!,
    eventName: parts.slice(2).join('.'),
  }
}

function parseAppEventName(eventType: string): string | null {
  return parseAppEventType(eventType)?.eventName ?? null
}

function labelFromPath(path: string): string {
  const segment = path.split('.').pop() ?? path
  return segment
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

function buildFieldsFromRecord(
  record: Record<string, unknown>,
  pathPrefix: string,
): EventContextField[] {
  const fields: EventContextField[] = []

  for (const [key, value] of Object.entries(record)) {
    const path = `${pathPrefix}.${key}`

    if (value != null && typeof value === 'object' && !Array.isArray(value)) {
      fields.push({
        path,
        label: labelFromPath(path),
        type: 'object',
        children: buildFieldsFromRecord(value as Record<string, unknown>, path),
      })
      continue
    }

    if (Array.isArray(value)) {
      fields.push({
        path,
        label: labelFromPath(path),
        type: 'array',
        description:
          value.length > 0 ? `Array (${typeof value[0]})` : 'Array',
      })
      continue
    }

    fields.push({
      path,
      label: labelFromPath(path),
      type: value === null ? 'null' : typeof value,
    })
  }

  return fields
}

/**
 * Build liquid context fields and sample envelope from an app event catalog example.
 */
export function buildAppEventContextFromExample(
  appHandle: string,
  eventName: string,
  examplePayload: Record<string, unknown>,
): {
  fields: EventContextField[]
  sampleEnvelope: Record<string, unknown>
} {
  const eventType = `app.${appHandle}.${eventName}`
  const correlationId =
    typeof examplePayload.trace_id === 'string'
      ? examplePayload.trace_id
      : 'trace_example'

  const dataExample: Record<string, unknown> = {
    ...examplePayload,
    eventName,
    eventType,
    appInstallationId: 'app_installation_example',
  }

  const dataSection: EventContextField = {
    path: 'data',
    label: 'Event data',
    type: 'object',
    children: buildFieldsFromRecord(dataExample, 'data'),
  }

  const sampleEnvelope: Record<string, unknown> = {
    resource: { type: 'app', id: 'app_installation_example' },
    actor: { type: 'system', name: 'webhook', isHuman: false },
    data: dataExample,
    meta: {
      timestamp: '2026-01-01T00:00:00.000Z',
      correlationId,
    },
  }

  return {
    fields: [...ENVELOPE_FIELDS, dataSection],
    sampleEnvelope,
  }
}

export function buildAppEventContextFromCatalogFields(
  dataFields: EventContextField[],
  sampleEnvelope?: Record<string, unknown>,
): {
  fields: EventContextField[]
  sampleEnvelope: Record<string, unknown> | undefined
} {
  const dataSection: EventContextField = {
    path: 'data',
    label: 'Event data',
    type: 'object',
    children: dataFields,
  }

  return {
    fields: [...ENVELOPE_FIELDS, dataSection],
    sampleEnvelope,
  }
}

export function parseAppEventTypeName(
  eventType: string,
): { appHandle: string; eventName: string } | null {
  return parseAppEventType(eventType)
}

function getAppEventDataFields(_eventName: string): EventContextField[] {
  // App-specific payload fields come from the installing app's event catalog
  // (contextFields / examples). Do not hardcode vendor-shaped fallbacks here.
  return [...APP_EVENT_BASE]
}

export function flattenContextFields(
  fields: EventContextField[],
  prefix = '',
): Array<EventContextField & { fullPath: string }> {
  const result: Array<EventContextField & { fullPath: string }> = []

  for (const field of fields) {
    const fullPath = field.path
    result.push({ ...field, fullPath })

    if (field.children?.length) {
      result.push(...flattenContextFields(field.children, prefix))
    }
  }

  return result
}

export function getEventContextFields(eventType: string): EventContextField[] {
  const dataSection: EventContextField = {
    path: 'data',
    label: 'Event data',
    type: 'object',
    children: [],
  }

  const appEventName = parseAppEventName(eventType)
  if (appEventName) {
    dataSection.children = getAppEventDataFields(appEventName)
    return [...ENVELOPE_FIELDS, dataSection]
  }

  const knownData = EVENT_DATA_FIELDS[eventType]
  if (knownData) {
    dataSection.children = knownData
    return [...ENVELOPE_FIELDS, dataSection]
  }

  dataSection.description = 'Event-specific payload fields'
  return [...ENVELOPE_FIELDS, dataSection]
}

export function getEventContextSample(eventType: string): Record<string, unknown> | undefined {
  if (SAMPLE_ENVELOPES[eventType]) {
    return SAMPLE_ENVELOPES[eventType]
  }

  const appEventName = parseAppEventName(eventType)
  if (appEventName) {
    // Prefer catalog examples via buildAppEventContextFromExample when available.
    return {
      resource: { type: 'app', id: 'app_example' },
      actor: { type: 'system', name: 'app', isHuman: false },
      data: {
        eventName: appEventName,
        eventType,
        appInstallationId: 'app_installation_example',
      },
      meta: { timestamp: '2026-01-01T00:00:00.000Z' },
    }
  }

  return undefined
}
